import React from 'react';
import { DroneEntity } from '@src/core/features';

/**
 * Hook to compute selected drone and mission status
 * @param selected - Currently selected drone from Redux
 * @param flyingDrones - Array of drones that are currently flying (with updated positions)
 * @param missionDrones - Array of drones that are on a mission
 * @param idleDrones - Array of idle drones (optional, for finding drone when it transitions from mission to idle)
 * @returns Object containing selectedDrone (merged with flying data if available) and isOnMission flag
 */
export const useDroneSelection = (
  selected: DroneEntity | null,
  flyingDrones: DroneEntity[],
  missionDrones: DroneEntity[],
  idleDrones?: DroneEntity[]
) => {
  const selectedDrone = React.useMemo(() => {
    if (!selected) return null;
    
    // First try to find in flyingDrones (most up-to-date with flight data)
    const flyingDrone = flyingDrones.find((drone) => drone.serialNumber === selected.serialNumber);
    if (flyingDrone) return flyingDrone;
    
    // Then try to find in missionDrones (if on mission but no flight data yet)
    const missionDrone = missionDrones.find((drone) => drone.serialNumber === selected.serialNumber);
    if (missionDrone) return missionDrone;
    
    // Then try to find in idleDrones (if drone transitioned from mission to idle)
    if (idleDrones) {
      const idleDrone = idleDrones.find((drone) => drone.serialNumber === selected.serialNumber);
      if (idleDrone) return idleDrone;
    }
    
    // Otherwise return the selected drone from Redux
    return selected;
  }, [flyingDrones, missionDrones, idleDrones, selected]);

  const isOnMission = React.useMemo(() => {
    return !!missionDrones.find((drone) => drone.serialNumber === selected?.serialNumber);
  }, [missionDrones, selected?.serialNumber]);

  return { selectedDrone, isOnMission };
};

